import React, { FC, ReactNode, useContext } from "react";

import { Accordion, AccordionContext, Stack, StackProps, useAccordionButton } from "react-bootstrap";
import { AccordionProps } from "react-bootstrap";

import classNames from "classnames";
import { ReactSVG } from "react-svg";

import { ArrowIcon } from "@assets/icons";

import styles from "./CustomAccordion.module.scss";

interface CustomAccordionHeaderProps {
  header?: ReactNode;
}
interface CustomAccordionContainerProps extends StackProps, CustomAccordionHeaderProps {
  eventKey: string;
  contrastOpenStyle?: boolean;
  customHeaderStyle?: string;
  callback?: (eventKey: string) => void;
  children?: React.ReactNode;
  preventCloseOnContentClick?: boolean;
}
export const CustomAccordionContainer: FC<CustomAccordionContainerProps> = ({
  header,
  contrastOpenStyle,
  customHeaderStyle,
  children,
  eventKey,
  callback,
  preventCloseOnContentClick = false,
  ...rest
}) => {
  const { activeEventKey } = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(eventKey, () => callback && callback(eventKey));

  const isCurrentEventKey = activeEventKey === eventKey;

  const handleContentClick = (event: React.MouseEvent) => {
    if (preventCloseOnContentClick) {
      event.stopPropagation();
    }
  };

  return (
    <Stack
      className={classNames({
        [styles["accordion-open"]]: isCurrentEventKey && contrastOpenStyle,
        [styles["accordion-close"]]: !isCurrentEventKey && contrastOpenStyle,
      })}
      {...rest}
    >
      <Stack direction="horizontal" className={classNames(styles["accordion-row"], customHeaderStyle, "b3")}>
        {header}
        <ReactSVG
          className={classNames("ms-auto", {
            [styles["rotate-icon"]]: !isCurrentEventKey,
            [styles["normal-icon"]]: isCurrentEventKey,
          })}
          src={ArrowIcon}
          onClick={decoratedOnClick}
        />
      </Stack>
      <div onClick={handleContentClick}>{children}</div>
    </Stack>
  );
};

interface CustomAccordionProps extends CustomAccordionHeaderProps, AccordionProps {
  accordionKey: string;
  children?: ReactNode;
  contrastOpenStyle?: boolean;
  customHeaderStyle?: string;
  defaultActiveKey?: string;
  preventCloseOnContentClick?: boolean;
  showBottomLine?: boolean;
}

export const CustomAccordion: FC<CustomAccordionProps> = ({
  accordionKey,
  contrastOpenStyle = true,
  customHeaderStyle,
  header,
  children,
  defaultActiveKey,
  preventCloseOnContentClick = false,
  showBottomLine = false,
  ...rest
}) => {
  return (
    <Accordion key={accordionKey} defaultActiveKey={defaultActiveKey} {...rest}>
      <CustomAccordionContainer
        eventKey={accordionKey}
        header={header}
        contrastOpenStyle={contrastOpenStyle}
        customHeaderStyle={customHeaderStyle}
        preventCloseOnContentClick={preventCloseOnContentClick}
      >
        <Accordion.Collapse eventKey={accordionKey}>
          <>{children}</>
        </Accordion.Collapse>
      </CustomAccordionContainer>
      {showBottomLine && <hr className="mt-4" />}
    </Accordion>
  );
};
